import { useState } from 'react';
import type { ProfileUser } from '@/mocks/profile';

interface Props {
  user: ProfileUser;
  onRefresh?: () => void;
  refreshing?: boolean;
  onSettings?: () => void;
}

export default function ProfileHeader({ user, onRefresh, refreshing, onSettings }: Props) {
  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(user.inviteCode).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative bg-primary-500 px-5 pt-6 pb-8 overflow-hidden">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700" />
      <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-3.5">
          {/* Avatar with white ring */}
          <div className="w-14 h-14 rounded-full bg-white border-2 border-white/30 flex items-center justify-center overflow-hidden shadow-lg">
            {user.avatar ? (
              <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <i className="ri-user-3-line text-primary-500 text-2xl" />
            )}
          </div>
          {/* Info */}
          <div>
            <div className="flex items-center gap-2">
              <p className="text-white text-lg font-bold tracking-wide">{user.name}</p>
              {user.phone && (
                <span className="px-1.5 py-0.5 bg-white/15 rounded text-white/80 text-[10px] font-medium tracking-wide">
                  {user.phone}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-white/70 text-xs">邀请码：{user.inviteCode}</span>
              <button
                onClick={copyCode}
                className="flex items-center gap-1 px-2 py-0.5 bg-white/15 rounded-full text-white/90 text-xs cursor-pointer hover:bg-white/25 transition-colors active:scale-95"
              >
                <i className="ri-file-copy-line text-xs" />
                复制
              </button>
            </div>
            {/* 实名认证 */}
            <div className="flex items-center gap-1.5 mt-1.5">
              <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
                <i className="ri-shield-check-fill text-white text-xs" />
              </div>
              <span className="text-white/80 text-xs">实名认证</span>
              <span className="px-1.5 py-0.5 bg-white/20 rounded text-white text-[10px] font-medium">已认证</span>
            </div>
          </div>
        </div>

        {/* Settings + Refresh icons（竖排：设置在上，刷新在下） */}
        <div className="flex flex-col items-center gap-4">
          <button
            onClick={onSettings}
            className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white/80 cursor-pointer hover:bg-white/25 transition-colors active:scale-95"
            aria-label="设置"
          >
            <i className="ri-settings-3-line text-lg" />
          </button>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white/80 cursor-pointer hover:bg-white/25 transition-colors active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="刷新"
          >
            <i className={refreshing ? 'ri-refresh-line text-lg animate-spin' : 'ri-refresh-line text-lg'} />
          </button>
        </div>
      </div>

      {/* Copy toast */}
      {copied && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white text-xs z-10">
          已复制邀请码
        </div>
      )}
    </div>
  );
}